import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import { useAuthStore } from '@/stores/authStore';
import type {
  Organization,
  OrganizationCreate,
  OrganizationInvite,
  OrganizationInviteAcceptResponse,
  OrganizationInviteCreate,
  OrganizationInviteListResponse,
  OrganizationInvitePublic,
  OrganizationListResponse,
  OrganizationMemberAdd,
  OrganizationNoteAdd,
  OrganizationUpdate,
} from '../types';

// Query keys are scoped by the current user's id so the singleton React Query
// cache can't leak one user's teams into a different user's session
// (sign-out/sign-in on the same tab). Invalidations use the base prefix so
// they still match the user-scoped key via React Query's prefix matching.
const ORGANIZATIONS_QUERY_KEY = ['organizations'];

type EmptyObject = Record<string, never>;

/**
 * Hook for managing organizations.
 * Provides CRUD operations for organizations including member and note management.
 */
export function useOrganizations(enabled = true) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.userData?.id);

  // Fetch all organizations for the current user
  const {
    data: organizationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...ORGANIZATIONS_QUERY_KEY, userId],
    queryFn: async () => {
      const response = await noteGoApiFetch<
        OrganizationListResponse,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        EmptyObject
      >({
        url: '/organizations/',
        method: 'GET',
      });
      return response;
    },
    // Wait for a concrete user id before firing — prevents writing one user's
    // teams into the cache slot keyed by `undefined` during auth hydration,
    // which could otherwise be served to a different user on the same tab.
    enabled: enabled && !!userId,
    staleTime: 30000, // 30 seconds
  });

  // Create organization mutation
  const createMutation = useMutation({
    mutationFn: async (data: OrganizationCreate) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        OrganizationCreate,
        EmptyObject,
        EmptyObject,
        EmptyObject
      >({
        url: '/organizations/',
        method: 'POST',
        body: data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Rename organization mutation (owner-only on the server)
  const renameMutation = useMutation({
    mutationFn: async ({ organizationId, name }: { organizationId: string; name: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        OrganizationUpdate,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}',
        method: 'PUT',
        body: { name },
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Leave organization mutation (self-leave, member-only)
  const leaveMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      await noteGoApiFetch<void, unknown, undefined, EmptyObject, EmptyObject, { organization_id: string }>({
        url: '/organizations/{organization_id}/members/me',
        method: 'DELETE',
        pathParams: { organization_id: organizationId },
      });
      return organizationId;
    },
    onSuccess: (organizationId) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      // Purge org-scoped caches so the user can't see stale team data via Back button
      queryClient.removeQueries({ queryKey: ['organization', userId, organizationId] });
      queryClient.removeQueries({ queryKey: ['organization-notes', userId, organizationId] });
      // Folders are org-scoped too — leaving them cached kept folder names and
      // member_emails for a team the user no longer belongs to.
      queryClient.removeQueries({ queryKey: ['organization-folders', userId, organizationId] });
    },
  });

  // Delete organization mutation
  const deleteMutation = useMutation({
    mutationFn: async (organizationId: string) => {
      await noteGoApiFetch<void, unknown, undefined, EmptyObject, EmptyObject, { organization_id: string }>({
        url: '/organizations/{organization_id}',
        method: 'DELETE',
        pathParams: { organization_id: organizationId },
      });
      return organizationId;
    },
    onSuccess: (organizationId) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      // Purge org-scoped caches so the Back button can't reopen a deleted team
      // with stale data while the 30s staleTime window is still active.
      queryClient.removeQueries({ queryKey: ['organization', userId, organizationId] });
      queryClient.removeQueries({ queryKey: ['organization-notes', userId, organizationId] });
      // Folders are org-scoped too — leaving them cached kept folder names and
      // member_emails for a team the user no longer belongs to.
      queryClient.removeQueries({ queryKey: ['organization-folders', userId, organizationId] });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async ({ organizationId, email }: { organizationId: string; email: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        OrganizationMemberAdd,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/members',
        method: 'POST',
        body: { email },
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async ({ organizationId, email }: { organizationId: string; email: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; member_email: string }
      >({
        url: '/organizations/{organization_id}/members/{member_email}',
        method: 'DELETE',
        pathParams: { organization_id: organizationId, member_email: email },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Promote a member to admin (owner or an existing admin on the server)
  const promoteAdminMutation = useMutation({
    mutationFn: async ({ organizationId, email }: { organizationId: string; email: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; member_email: string }
      >({
        url: '/organizations/{organization_id}/admins/{member_email}',
        method: 'PUT',
        pathParams: { organization_id: organizationId, member_email: email },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Demote an admin back to a regular member
  const demoteAdminMutation = useMutation({
    mutationFn: async ({ organizationId, email }: { organizationId: string; email: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; member_email: string }
      >({
        url: '/organizations/{organization_id}/admins/{member_email}',
        method: 'DELETE',
        pathParams: { organization_id: organizationId, member_email: email },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
    },
  });

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async ({ organizationId, noteId }: { organizationId: string; noteId: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        OrganizationNoteAdd,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/notes',
        method: 'POST',
        body: { note_id: noteId },
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      // Also refresh the single-org and org-notes caches. The team notes view
      // reads note_ids from ['organization', userId, orgId], which then keys the
      // ['organization-notes', ...] query — invalidating only the org *list*
      // above leaves these stale, so a newly added note wouldn't appear until
      // staleTime expired or a manual page refresh.
      queryClient.invalidateQueries({ queryKey: ['organization', userId, organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization-notes', userId, organizationId] });
    },
  });

  // Remove note mutation
  const removeNoteMutation = useMutation({
    mutationFn: async ({ organizationId, noteId }: { organizationId: string; noteId: string }) => {
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string; note_id: string }
      >({
        url: '/organizations/{organization_id}/notes/{note_id}',
        method: 'DELETE',
        pathParams: { organization_id: organizationId, note_id: noteId },
      });
      return response;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      // Keep the single-org and org-notes caches in sync so a removed note
      // disappears from the team view immediately (same keys as addNote above).
      queryClient.invalidateQueries({ queryKey: ['organization', userId, organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization-notes', userId, organizationId] });
    },
  });

  // Invite member mutation (new — replaces direct addMember for invite flow)
  const inviteMemberMutation = useMutation({
    mutationFn: async ({
      organizationId,
      email,
      message,
    }: {
      organizationId: string;
      email: string;
      message?: string;
    }) => {
      const body: OrganizationInviteCreate = { email };
      if (message && message.trim()) {
        body.message = message.trim();
      }
      const response = await noteGoApiFetch<
        OrganizationInvite,
        unknown,
        OrganizationInviteCreate,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/invites',
        method: 'POST',
        body,
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites', userId, vars.organizationId] });
    },
  });

  // Revoke invite mutation
  const revokeInviteMutation = useMutation({
    mutationFn: async ({ token, organizationId }: { token: string; organizationId: string }) => {
      await noteGoApiFetch<void, unknown, undefined, EmptyObject, EmptyObject, { token: string }>({
        url: '/organizations/invites/{token}',
        method: 'DELETE',
        pathParams: { token },
      });
      return organizationId;
    },
    onSuccess: (organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['organization-invites', userId, organizationId] });
    },
  });

  const organizations = organizationsData?.organizations ?? [];
  const isMutating =
    createMutation.isPending ||
    renameMutation.isPending ||
    leaveMutation.isPending ||
    deleteMutation.isPending ||
    addMemberMutation.isPending ||
    removeMemberMutation.isPending ||
    promoteAdminMutation.isPending ||
    demoteAdminMutation.isPending ||
    addNoteMutation.isPending ||
    removeNoteMutation.isPending ||
    inviteMemberMutation.isPending ||
    revokeInviteMutation.isPending;

  return {
    organizations,
    isLoading,
    isMutating,
    error,
    refetch,
    createOrganization: createMutation.mutateAsync,
    renameOrganization: renameMutation.mutateAsync,
    leaveOrganization: leaveMutation.mutateAsync,
    deleteOrganization: deleteMutation.mutateAsync,
    addMember: addMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    promoteAdmin: promoteAdminMutation.mutateAsync,
    demoteAdmin: demoteAdminMutation.mutateAsync,
    addNote: addNoteMutation.mutateAsync,
    removeNote: removeNoteMutation.mutateAsync,
    inviteMember: inviteMemberMutation.mutateAsync,
    revokeInvite: revokeInviteMutation.mutateAsync,
  };
}

/**
 * List pending/accepted/revoked/expired invites for an organization (owner only).
 */
export function useOrganizationInvites(organizationId: string | undefined, enabled = true) {
  const userId = useAuthStore((state) => state.userData?.id);
  return useQuery({
    queryKey: ['organization-invites', userId, organizationId],
    queryFn: async () => {
      if (!organizationId) return { invites: [] as OrganizationInvite[] };
      const response = await noteGoApiFetch<
        OrganizationInviteListResponse,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}/invites',
        method: 'GET',
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    enabled: enabled && !!organizationId && !!userId,
    staleTime: 15000,
  });
}

/**
 * Public read of an invite by token — for the AcceptInvite page.
 * No auth required server-side.
 */
export function usePublicInvite(token: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['public-invite', token],
    queryFn: async () => {
      if (!token) return null;
      const response = await noteGoApiFetch<
        OrganizationInvitePublic,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { token: string }
      >({
        url: '/organizations/invites/{token}',
        method: 'GET',
        pathParams: { token },
      });
      return response;
    },
    enabled: enabled && !!token,
    retry: false,
  });
}

/**
 * Mutation hook for accepting an invite by token (authenticated).
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const response = await noteGoApiFetch<
        OrganizationInviteAcceptResponse,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { token: string }
      >({
        url: '/organizations/invites/{token}/accept',
        method: 'POST',
        pathParams: { token },
      });
      return response;
    },
    onSuccess: (_, token) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // Mark the public-invite cache stale so any lingering view of the
      // invite preview (e.g. interrupted navigation, back button) refetches
      // and reflects status: 'accepted' instead of 'pending'.
      queryClient.invalidateQueries({ queryKey: ['public-invite', token] });
    },
  });
}

/**
 * Hook for fetching a single organization by ID.
 */
export function useOrganization(organizationId: string | undefined, enabled = true) {
  const userId = useAuthStore((state) => state.userData?.id);
  return useQuery({
    queryKey: ['organization', userId, organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      const response = await noteGoApiFetch<
        Organization,
        unknown,
        undefined,
        EmptyObject,
        EmptyObject,
        { organization_id: string }
      >({
        url: '/organizations/{organization_id}',
        method: 'GET',
        pathParams: { organization_id: organizationId },
      });
      return response;
    },
    enabled: enabled && !!organizationId && !!userId,
    staleTime: 30000,
  });
}
